import React, { useState, useEffect } from 'react';
import { X, Camera, MapPin, ChevronRight, CheckCircle, Loader2, Calendar, Clock, User, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { Dialog, DialogContent } from './ui/dialog';
import type { IncidentCreate } from '../types';
import { logger } from '../lib/logger';
import { 
  userLocationAtom, 
  locationAccuracyAtom, 
  isHighAccuracyAtom,
  locationZoneAtom
} from '../atoms/location';
import { isInHaiti } from '../utils/locationUtils';

interface ReportIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (incident: IncidentCreate) => Promise<void>;
}

async function getLocationDetails(lat: number, lon: number): Promise<string> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse.php?lat=${lat}&lon=${lon}&zoom=18&format=jsonv2`,
      {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'RapoKrim/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch location details');
    }

    const data = await response.json();
    
    const address = data.address;
    const location = [
      address?.house_number,
      address?.road,
      address?.suburb,
      address?.city || address?.state || 'Haiti'
    ].filter(Boolean).join(', ');

    return location;
  } catch (error) {
    logger.error(error, 'geocoding');
    // Fallback to coordinates if service fails
    return `${lat.toFixed(4)}°N, ${lon.toFixed(4)}°W`;
  }
}

export default function ReportIncidentModal({ isOpen, onClose, onSubmit }: ReportIncidentModalProps) {
  const { t } = useTranslation();
  
  const [userLocation] = useAtom(userLocationAtom);
  const [accuracy] = useAtom(locationAccuracyAtom);
  const [isHighAccuracy] = useAtom(isHighAccuracyAtom);
  const [locationZone, setLocationZone] = useAtom(locationZoneAtom);
  
  const [currentStep, setCurrentStep] = useState(0);
  
  const [incidentType, setIncidentType] = useState<string | null>(null);
  const [severity, setSeverity] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Add a state to track if user is in Haiti
  const [isUserInHaiti, setIsUserInHaiti] = useState<boolean | null>(null);

  // Calculate total steps based on incident type
  const getTotalSteps = () => {
    return incidentType === 'MISSING_PERSON' ? 5 : 4;
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setIncidentType(null);
      setSeverity(null);
      setDescription('');
      setFiles([]);
      setFilePreviews([]);
      setIsSubmitted(false);
      setError('');
    }
  }, [isOpen]);
  
  useEffect(() => {
    if (!isOpen || !userLocation) return;
    
    // Check if user is in Haiti
    setIsUserInHaiti(isInHaiti(userLocation.lat, userLocation.lng));
    
    // Update zone whenever userLocation changes and modal is open
    getLocationDetails(userLocation.lat, userLocation.lng)
      .then(zone => {
        setLocationZone(zone);
      })
      .catch(error => {
        logger.error(error, 'getLocationDetails');
        // Fallback to coordinates if geocoding fails
        setLocationZone(`${userLocation.lat.toFixed(4)}°N, ${userLocation.lng.toFixed(4)}°W`);
      });
  }, [isOpen, userLocation, setLocationZone]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles(prevFiles => [...prevFiles, ...newFiles]);
      
      // Create previews
      const newPreviews = newFiles.map(file => URL.createObjectURL(file));
      setFilePreviews(prevPreviews => [...prevPreviews, ...newPreviews]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    
    // Revoke the object URL to avoid memory leaks
    URL.revokeObjectURL(filePreviews[index]);
    setFilePreviews(prevPreviews => prevPreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!incidentType) {
      setError(t('incident.report.error.type'));
      return;
    }
    
    if (!severity) {
      setError(t('incident.report.error.severity'));
      return;
    }
    
    if (!userLocation) {
      setError(t('incident.report.error.location'));
      return;
    }
    
    if (!description || description.trim() === '') {
      setError(t('incident.report.error.description'));
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    try {
      // Simplified to use the standard description field
      const incidentData: IncidentCreate = {
        type: incidentType as IncidentCreate['type'],
        severity: severity as IncidentCreate['severity'],
        description: description.trim(),
        location: {
          type: "Point",
          coordinates: [userLocation.lng, userLocation.lat]
        },
        location_zone: locationZone,
        anonymous: true,
        media: files
      };
      
      await onSubmit(incidentData);
      setIsSubmitted(true);
      
      // Reset and close after showing success
      setTimeout(() => {
        setCurrentStep(0);
        setIncidentType(null);
        setSeverity(null);
        setDescription('');
        setFiles([]);
        setFilePreviews([]);
        setIsSubmitted(false);
        onClose();
      }, 2000);
    } catch (err) {
      logger.error(err, 'submitReport');
      setError(err instanceof Error ? err.message : t('incident.report.error'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleClose = () => {
    // Clean up file previews
    filePreviews.forEach(preview => URL.revokeObjectURL(preview));
    onClose();
  };

  // Define incident types and severity levels
  const incidentTypes = [
    { id: 'GANG_ACTIVITY', icon: '👥', label: t('incident.types.GANG_ACTIVITY') },
    { id: 'SEXUAL_VIOLENCE', icon: '⚠️', label: t('incident.types.SEXUAL_VIOLENCE') },
    { id: 'CIVIL_UNREST', icon: '🔥', label: t('incident.types.CIVIL_UNREST') },
    { id: 'KIDNAPPING', icon: '🚨', label: t('incident.types.KIDNAPPING') },
    { id: 'ROBBERY', icon: '💰', label: t('incident.types.ROBBERY') },
    { id: 'NATURAL_DISASTER', icon: '🌪️', label: t('incident.types.NATURAL_DISASTER') },
    { id: 'ROAD_CLOSURE', icon: '🚧', label: t('incident.types.ROAD_CLOSURE') },
    { id: 'MISSING_PERSON', icon: '🔍', label: t('incident.types.MISSING_PERSON') },
  ];
  
  const severityLevels = [
    { id: 'CRITICAL', color: '#FF375F', label: t('incident.severity.CRITICAL') },
    { id: 'HIGH', color: '#FF7A00', label: t('incident.severity.HIGH') },
    { id: 'MODERATE', color: '#FFBD00', label: t('incident.severity.MODERATE') },
    { id: 'LOW', color: '#32D74B', label: t('incident.severity.LOW') },
  ];

  const renderStep = () => {
    switch(currentStep) {
      case 0:
        return (
          <div className="flex flex-col h-full bg-white">
            <div className="text-center py-4">
              <h2 className="text-xl font-bold">{t('incident.report.type.question')}</h2>
              <p className="text-gray-500 text-sm">{t('incident.report.type.instruction')}</p>
            </div>
            <div className="flex-1 overflow-y-auto px-2">
              <div className="grid grid-cols-2 gap-2">
                {incidentTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setIncidentType(type.id);
                      setCurrentStep(1);
                    }}
                    className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 ${
                      incidentType === type.id ? 'border-red-500 bg-red-50' : 'border-gray-200'
                    }`}
                  >
                    <span className="text-4xl mb-2">{type.icon}</span>
                    <span className="text-sm font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      
      case 1:
        return (
          <div className="flex flex-col h-full bg-white">
            <div className="text-center py-4">
              <h2 className="text-xl font-bold">{t('incident.report.severity.question')}</h2>
              <p className="text-gray-500 text-sm">{t('incident.report.severity.instruction')}</p>
            </div>
            <div className="flex-1 overflow-y-auto px-4">
              {severityLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => {
                    setSeverity(level.id);
                    // If it's a missing person report, go to the missing person form
                    if (incidentType === 'MISSING_PERSON') {
                      setCurrentStep(2);
                    } else {
                      // Otherwise, go to the regular description step
                      setCurrentStep(2);
                    }
                  }}
                  className={`flex items-center justify-between w-full p-4 mb-3 rounded-xl border-2 ${
                    severity === level.id ? 'border-red-500' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: level.color }}></div>
                    <span className="font-medium">{level.label}</span>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
              ))}
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="flex flex-col h-full bg-white">
            <div className="text-center py-4">
              <h2 className="text-xl font-bold">{t('incident.report.description.title')}</h2>
              <p className="text-gray-500 text-sm">{t('incident.report.description.instruction')}</p>
            </div>
            <div className="flex-1 overflow-y-auto px-4">
              {/* Add missing person guidance when incident type is MISSING_PERSON */}
              {incidentType === 'MISSING_PERSON' && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-medium text-blue-800 mb-2">{t('incident.report.missingPerson.guidance.title')}</h3>
                  <p className="text-sm text-blue-700 mb-2">{t('incident.report.missingPerson.guidance.description')}</p>
                  <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
                    <li>{t('incident.report.missingPerson.guidance.name')}</li>
                    <li>{t('incident.report.missingPerson.guidance.age')}</li>
                    <li>{t('incident.report.missingPerson.guidance.lastSeen')}</li>
                    <li>{t('incident.report.missingPerson.guidance.appearance')}</li>
                    <li>{t('incident.report.missingPerson.guidance.clothing')}</li>
                    <li>{t('incident.report.missingPerson.guidance.distinctive')}</li>
                    <li>{t('incident.report.missingPerson.guidance.medical')}</li>
                    <li>{t('incident.report.missingPerson.guidance.contact')}</li>
                  </ul>
                </div>
              )}
              
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  incidentType === 'MISSING_PERSON' 
                    ? t('incident.report.missingPerson.descriptionPlaceholder')
                    : t('incident.report.description.placeholder')
                }
                className="w-full p-4 border-2 border-gray-200 rounded-xl h-56 focus:border-red-500 focus:ring-red-500 resize-none"
              />
              
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="bg-red-600 text-white font-medium py-2 px-6 rounded-xl hover:bg-red-700"
                >
                  {t('incident.report.next')}
                </button>
              </div>
            </div>
          </div>
        );
        
      case 3:
        // For missing person, this is the media upload step
        // For other incidents, this is also the media upload step
        return (
          <div className="flex flex-col h-full bg-white">
            <div className="text-center py-4">
              <h2 className="text-xl font-bold">{t('incident.report.media.title')}</h2>
              <p className="text-gray-500 text-sm">
                {incidentType === 'MISSING_PERSON' 
                  ? t('incident.report.missingPerson.uploadPhoto') 
                  : t('incident.report.media.instruction')}
              </p>
            </div>
            <div className="flex-1 px-4 overflow-y-auto">
              <div className="grid grid-cols-3 gap-2 mb-4">
                {filePreviews.map((preview, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                    <button 
                      className="absolute top-1 right-1 bg-black bg-opacity-50 rounded-full p-1"
                      onClick={() => removeFile(index)}
                    >
                      <X size={14} className="text-white" />
                    </button>
                  </div>
                ))}
                <label className="aspect-square flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 cursor-pointer">
                  <div className="flex flex-col items-center">
                    <Camera size={24} className="text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">{t('incident.report.media.add')}</span>
                  </div>
                  <input 
                    type="file" 
                    accept="image/*,video/*" 
                    onChange={handleFileChange} 
                    className="hidden" 
                    multiple 
                  />
                </label>
              </div>
              
              <div className="bg-gray-100 rounded-xl p-4 mb-4">
                <div className="flex items-center">
                  <MapPin size={20} className="text-red-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium">{t('incident.report.location.title')}</p>
                    {userLocation ? (
                      <>
                        <p className="text-xs text-gray-500">
                          {userLocation.lat.toFixed(6)}, {userLocation.lng.toFixed(6)}
                          {accuracy && ` (±${Math.round(accuracy)}m)`}
                        </p>
                        {locationZone && (
                          <p className="text-xs font-medium mt-1">{locationZone}</p>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-gray-500">{t('incident.report.location.detecting')}</p>
                    )}
                  </div>
                </div>
              </div>
              
              {error && (
                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl mb-4">
                  {error}
                </div>
              )}
              
              <div className="text-sm text-gray-600 italic mb-4">
                {t('incident.report.alwaysAnonymous')}
              </div>
              
              <div className="flex justify-end mb-4">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !userLocation || (userLocation && isUserInHaiti === false)}
                  className="bg-red-600 text-white font-medium py-2 px-6 rounded-xl flex items-center justify-center hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <Loader2 size={20} className="mr-2 animate-spin" />
                      <span>{t('incident.report.submitting')}</span>
                    </div>
                  ) : isSubmitted ? (
                    <div className="flex items-center">
                      <CheckCircle size={20} className="mr-2" />
                      <span>{t('incident.report.submitted')}</span>
                    </div>
                  ) : userLocation && isUserInHaiti === false ? (
                    <span>{t('onlyInHaiti')}</span>
                  ) : (
                    <span>{t('incident.report.submit')}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="p-0 max-w-md h-[85vh] max-h-[700px] flex flex-col overflow-hidden bg-white">
        <div className="px-4 py-3 border-b flex items-center justify-between bg-white">
          <button 
            onClick={() => currentStep > 0 && setCurrentStep(currentStep - 1)} 
            className={`p-2 ${currentStep > 0 ? 'text-gray-900' : 'text-gray-300'}`}
            disabled={currentStep === 0}
          >
            {currentStep > 0 ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            ) : (
              <div className="w-6"></div>
            )}
          </button>
          <div className="flex-1 flex justify-center">
            <div className="flex space-x-1">
              {Array.from({ length: getTotalSteps() }).map((_, step) => (
                <div
                  key={step}
                  className={`h-1 rounded-full w-8 ${
                    step <= currentStep ? 'bg-red-600' : 'bg-gray-200'
                  }`}
                ></div>
              ))}
            </div>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 text-gray-500"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="flex-1 overflow-hidden bg-white">
          {renderStep()}
        </div>
      </DialogContent>
    </Dialog>
  );
}